import { useState, useEffect, useMemo } from "react";
import { Table, Button, Space, Popconfirm, message, Input, DatePicker, Select, Row, Col } from "antd";
import type { ColumnsType } from "antd/es/table";
import { ReloadOutlined, DeleteOutlined, EditOutlined, PlusOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { listBins, deleteBin, updateBinPrivate, getToken } from "../utils/api";
import type { BinListItem } from "../utils/api";
import { useLocale } from "../contexts/LocaleContext";
import { hashToFilters, updateHash, type FilterValues } from "../utils/urlHash";

const { RangePicker } = DatePicker;

interface BinTableProps {
  onEdit: (binId: string) => void;
  onCreateNew: () => void;
  refreshKey?: number;
}

export default function BinTable({ onEdit, onCreateNew, refreshKey }: BinTableProps) {
  const { t, locale } = useLocale();
  const [bins, setBins] = useState<BinListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<FilterValues>(() => hashToFilters());

  useEffect(() => {
    updateHash(filters);
  }, [filters]);

  useEffect(() => {
    const handleHashChange = () => {
      const newFilters = hashToFilters();
      setFilters(newFilters);
    };
    
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const loadBins = async () => {
    const token = getToken();
    if (!token) {
      message.warning(t.binTable.tokenWarning);
      return;
    }

    setLoading(true);
    try {
      const data = await listBins();
      setBins(Array.isArray(data) ? data : []);
    } catch (err) {
      message.error(err instanceof Error ? err.message : t.binTable.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBins();
  }, [refreshKey]);

  const handleDelete = async (binId: string) => {
    try {
      await deleteBin(binId);
      message.success(t.binTable.deleteSuccess);
      await loadBins();
    } catch (err) {
      message.error(err instanceof Error ? err.message : t.binTable.deleteFailed);
    }
  };

  const handleTogglePrivate = async (binId: string, currentPrivate: boolean) => {
    try {
      await updateBinPrivate(binId, !currentPrivate);
      message.success(t.binTable.togglePrivateSuccess);
      await loadBins();
    } catch (err) {
      message.error(err instanceof Error ? err.message : t.binTable.togglePrivateFailed);
    }
  };

  const handleFilterChange = (key: keyof FilterValues, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleReset = () => {
    const resetFilters: FilterValues = {
      name: "",
      record: "",
      dateRange: null,
      private: undefined,
    };
    setFilters(resetFilters);
    updateHash(resetFilters);
  };

  const filteredBins = useMemo(() => {
    return bins.filter((bin) => {
      if (filters.name) {
        const name = bin?.snippetMeta?.name || "";
        if (!name.toLowerCase().includes(filters.name.toLowerCase())) {
          return false;
        }
      }

      if (filters.record) {
        if (!bin.record.toLowerCase().includes(filters.record.toLowerCase())) {
          return false;
        }
      }

      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        const recordDate = dayjs(bin.createdAt);
        const start = filters.dateRange[0].startOf("day");
        const end = filters.dateRange[1].endOf("day");
        if (recordDate.isBefore(start) || recordDate.isAfter(end)) {
          return false;
        }
      }

      if (filters.private !== undefined) {
        if (bin.private !== filters.private) {
          return false;
        }
      }

      return true;
    });
  }, [bins, filters]);

  const columns: ColumnsType<BinListItem> = [
    {
      title: t.binTable.name,
      key: "name",
      render: (_, record) => (
        <div>
          {record?.snippetMeta?.name ? (
            <span className="font-medium">{record?.snippetMeta?.name}</span>
          ) : (
            <span className="italic" style={{ color: "var(--text-secondary)" }}>{t.common.unnamed}</span>
          )}
        </div>
      ),
    },
    {
      title: t.binTable.binId,
      dataIndex: "record",
      key: "record",
      render: (text: string) => <span className="font-mono text-sm">{text}</span>,
    },
    {
      title: t.binTable.createdAt,
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string) => new Date(text).toLocaleString(locale === "en" ? "en-US" : "zh-CN"),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: t.binTable.private,
      dataIndex: "private",
      key: "private",
      render: (private_: boolean) => (private_ ? t.common.yes : t.common.no),
      width: 80,
    },
    {
      title: t.binTable.operations,
      key: "action",
      width: 200,
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => onEdit(record.record)}>
            {t.common.edit}
          </Button>
          <Button
            type="link"
            icon={record.private ? <UnlockOutlined /> : <LockOutlined />}
            onClick={() => handleTogglePrivate(record.record, record.private)}
          >
            {record.private ? t.binTable.makePublic : t.binTable.makePrivate}
          </Button>
          <Popconfirm
            title={t.binTable.deleteConfirm}
            onConfirm={() => handleDelete(record.record)}
            okText={t.common.confirm}
            cancelText={t.common.cancel}
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              {t.common.delete}
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div className="bg-white rounded-lg border border-border">
      <div className="p-4 border-b border-border flex items-center justify-between">
        <h2 className="text-lg font-heading font-semibold text-text">{t.binTable.title}</h2>
        <Space>
          <Button onClick={handleReset}>{t.common.reset}</Button>
          <Button icon={<ReloadOutlined />} onClick={loadBins} loading={loading}>
            {t.common.refresh}
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={onCreateNew}>
            {t.binTable.createNew}
          </Button>
        </Space>
      </div>

      <div className="p-4 border-b border-border" style={{ backgroundColor: "var(--hover-bg)" }}>
        <Row gutter={16}>
          <Col span={6}>
            <div className="mb-2">
              <label className="text-sm mb-1 block" style={{ color: "var(--text-secondary)" }}>{t.binTable.name}:</label>
              <Input
                placeholder={t.binTable.namePlaceholder}
                value={filters.name}
                onChange={(e) => handleFilterChange("name", e.target.value)}
              />
            </div>
          </Col>
          <Col span={6}>
            <div className="mb-2">
              <label className="text-sm mb-1 block" style={{ color: "var(--text-secondary)" }}>{t.binTable.binId}:</label>
              <Input
                placeholder={t.binTable.idPlaceholder}
                value={filters.record}
                onChange={(e) => handleFilterChange("record", e.target.value)}
              />
            </div>
          </Col>
          <Col span={6}>
            <div className="mb-2">
              <label className="text-sm mb-1 block" style={{ color: "var(--text-secondary)" }}>{t.binTable.dateRangeLabel}:</label>
              <RangePicker
                value={filters.dateRange}
                onChange={(dates) => handleFilterChange("dateRange", dates)}
                style={{ width: "100%" }}
              />
            </div>
          </Col>
          <Col span={6}>
            <div className="mb-2">
              <label className="text-sm mb-1 block" style={{ color: "var(--text-secondary)" }}>{t.binTable.privateLabel}:</label>
              <Select
                value={filters.private}
                onChange={(value) => handleFilterChange("private", value)}
                placeholder={t.common.all}
                style={{ width: "100%" }}
                allowClear
              >
                <Select.Option value={true}>{t.common.yes}</Select.Option>
                <Select.Option value={false}>{t.common.no}</Select.Option>
              </Select>
            </div>
          </Col>
        </Row>
      </div>

      <div className="p-4">
        <Table
          columns={columns}
          dataSource={filteredBins}
          rowKey="record"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) =>
              locale === "en" ? `Total ${total} items` : `共 ${total} 条`,
          }}
        />
      </div>
    </div>
  );
}
